import { isWeb, View, XStack, YStack } from 'smoothie';

import { type Card, CardColor, CardDataType, CardForm } from '../../types/Card';
import CardData from '../cards/card-details/CardData';
import { FruitfulCardIcon } from '../cards/FruitfulCard';
import {
    DetailsTile,
    type DetailsTileAppearance,
    DetailsTileBackgroundColor,
    DetailsTileLabel,
    DetailsTileValue,
    useCopiedToast,
    useToggleSensitiveDataVisibility,
} from './DetailsTile';

export const DetailsTileForCard = ({
    card,
    accountName,
    appearance,
}: {
    card?: Card | null;
    accountName?: string | null;
    appearance?: DetailsTileAppearance;
}) => {
    const { showCopiedToast } = useCopiedToast();
    const { isSensitiveDataVisible, toggleSensitiveDataVisibility } =
        useToggleSensitiveDataVisibility();

    const physicalOrVirtualString =
        card?.form === CardForm.VIRTUAL ? 'Virtual' : 'Physical';

    const cardColor = card?.color ?? CardColor.BLUE;
    const cardLastFour = card?.lastFour ?? '****';
    const maskBackgroundColor =
        appearance?.maskBackgroundColor ?? DetailsTileBackgroundColor;

    if (card == null) {
        return null;
    }

    return (
        <DetailsTile
            card={card}
            icon={<FruitfulCardIcon form={card.form} color={cardColor} />}
            name={accountName ?? 'Fruitful Card'}
            description={`${physicalOrVirtualString} card`}
            isSensitiveDataVisible={isSensitiveDataVisible}
            toggleSensitiveDataVisibility={toggleSensitiveDataVisibility}
            appearance={appearance}
        >
            <CardDetail
                label="Card number"
                width={170}
                isSensitiveDataVisible={isSensitiveDataVisible}
                maskBackgroundColor={maskBackgroundColor}
                valueElement={
                    <CardData
                        cardDataType={CardDataType.COPY_PAN}
                        cardId={card.id}
                        onCopy={() => {
                            showCopiedToast();
                        }}
                    />
                }
                valueMaskedElement={
                    isWeb ? (
                        <DetailsTileValue
                            value={`**** **** **** ${cardLastFour}`}
                        />
                    ) : (
                        <>
                            <DetailsTileValue value="****" />
                            <DetailsTileValue value="****" />
                            <DetailsTileValue value="****" />
                            <DetailsTileValue value={cardLastFour} />
                        </>
                    )
                }
            />
            <CardDetail
                label="Exp"
                width={40}
                isSensitiveDataVisible={isSensitiveDataVisible}
                hideValueWhenMasked
                valueElement={
                    <CardData
                        cardDataType={CardDataType.EXP11}
                        cardId={card.id}
                    />
                }
                valueMasked="**/**"
            />
            <CardDetail
                label="CVV"
                width={28}
                isSensitiveDataVisible={isSensitiveDataVisible}
                hideValueWhenMasked
                valueElement={
                    <CardData
                        cardDataType={CardDataType.CVV11}
                        cardId={card.id}
                    />
                }
                valueMasked="***"
            />
        </DetailsTile>
    );
};

const CardDetail = ({
    label,
    width,
    isSensitiveDataVisible,
    valueElement,
    valueMaskedElement,
    valueMasked,
    maskBackgroundColor = DetailsTileBackgroundColor,
    hideValueWhenMasked = false,
}: {
    label: string;
    width: number;
    isSensitiveDataVisible: boolean;
    valueElement: React.ReactNode;
    valueMaskedElement?: React.ReactNode;
    valueMasked?: string;
    maskBackgroundColor?: string;
    /**
     * When true, the secure value layer is hidden (not just covered) while
     * masked, so the masked placeholder can render with no opaque background.
     * Only safe for cells without a Marqeta copy button (e.g. Exp / CVV); the
     * card number keeps its opaque cover so its copy button stays visible.
     */
    hideValueWhenMasked?: boolean;
}) => {
    const maskedDetailStyles = isSensitiveDataVisible
        ? { display: 'none' }
        : {};

    // Visually hide the secure value while masked so nothing leaks behind the
    // transparent placeholder. Use opacity (not display:none) so the Marqeta
    // iframe stays mounted AND rendered/loaded — otherwise it reloads on reveal
    // and the value surfaces with a noticeable delay.
    const valueElementStyles =
        hideValueWhenMasked && !isSensitiveDataVisible ? { opacity: 0 } : {};

    valueMaskedElement = valueMaskedElement ?? (
        <DetailsTileValue value={valueMasked ?? '***'} />
    );

    return (
        <YStack gap={2} width={width}>
            <DetailsTileLabel text={label} />
            {/* We're using positioning to layer the value and masked value so that the 'copy' button from marqeta can be visible for both */}
            <YStack position="relative" alignItems="flex-start">
                <View
                    width={width}
                    height={30}
                    position="absolute"
                    top={0}
                    style={valueElementStyles}
                >
                    {valueElement}
                </View>
                <XStack
                    display="inline-flex"
                    style={maskedDetailStyles}
                    gap={5}
                    maxWidth={width}
                    overflow="hidden"
                    backgroundColor={
                        hideValueWhenMasked
                            ? 'transparent'
                            : maskBackgroundColor
                    }
                >
                    {valueMaskedElement}
                </XStack>
            </YStack>
        </YStack>
    );
};
